import type { WebappResponse } from '@eventbrite/event-listing-service-client';
import { differenceInDays, parseISO } from 'date-fns';

export enum HighLevelStatus {
  ON_SALE = 'ON_SALE',
  SALES_ENDED = 'SALES_ENDED',
  SOLD_OUT = 'SOLD_OUT',
  UNAVAILABLE = 'UNAVAILABLE',
  POSTPONED = 'POSTPONED',
  CANCELLED = 'CANCELLED',
  SALES_START_SOON = 'SALES_START_SOON',
  UNKNOWN = 'UNKNOWN',
  EVENT_ENDED = 'EVENT_ENDED',
  COMPLETED = 'COMPLETED',
}

const DONATION_GRACE_PERIOD_DAYS = 45;

const MESSAGE_CODE_TO_STATUS: Record<string, HighLevelStatus> = {
  event_postponed: HighLevelStatus.POSTPONED,
  event_cancelled: HighLevelStatus.CANCELLED,
  tickets_sold_out: HighLevelStatus.SOLD_OUT,
  tickets_with_sales_ended: HighLevelStatus.SALES_ENDED,
  tickets_not_yet_on_sale: HighLevelStatus.SALES_START_SOON,
  tickets_unavailable: HighLevelStatus.UNAVAILABLE,
};

const SALES_STATUS_TO_STATUS: Record<string, HighLevelStatus> = {
  on_sale: HighLevelStatus.ON_SALE,
  sales_ended: HighLevelStatus.SALES_ENDED,
  sold_out: HighLevelStatus.SOLD_OUT,
};

export const getHighLevelStatus = (salesStatusData?: WebappResponse['salesStatus']): HighLevelStatus => {
  if (!salesStatusData) {
    return HighLevelStatus.UNKNOWN;
  }

  const { salesStatus, messageCode } = salesStatusData;

  return (
    (messageCode ? MESSAGE_CODE_TO_STATUS[messageCode] : undefined) ||
    (salesStatus ? SALES_STATUS_TO_STATUS[salesStatus] : undefined) ||
    HighLevelStatus.UNKNOWN
  );
};

export const shouldTreatAsLiveEvent = (
  highLevelStatus?: string,
  endDate?: string,
  hasDonationTicketsAvailable?: boolean,
): boolean => {
  if (!highLevelStatus) {
    return true;
  }

  if (
    [HighLevelStatus.EVENT_ENDED, HighLevelStatus.COMPLETED, HighLevelStatus.SALES_ENDED].includes(
      highLevelStatus as HighLevelStatus,
    ) &&
    endDate &&
    hasDonationTicketsAvailable
  ) {
    const parsedEndDate = parseISO(endDate);
    const daysSinceEnd = differenceInDays(new Date(), parsedEndDate);

    return daysSinceEnd < DONATION_GRACE_PERIOD_DAYS;
  }

  return (
    highLevelStatus === HighLevelStatus.ON_SALE ||
    highLevelStatus === HighLevelStatus.UNKNOWN ||
    highLevelStatus === HighLevelStatus.SOLD_OUT
  );
};
